// See messages/README.md for how to add strings.
// The /train page (pages/train.js) and its flow in components/train/: the
// upload step (TrainUploadStage, Sample, ClipEditor), the name step
// (TrainInfoStage), the demo under it (components/CustomModelDemo) and the
// failed voice's "Try again" (RetryTraining, shown in the library).
// Lengths arrive already formatted as clocks ("6:00") in {time}, {have} etc.
export const train = {
  en: {
    "train.seo.title": "Train Custom AI Voices | Jammable",
    "train.seo.description":
      "Train your own custom AI voices on Jammable. Upload audio files, train a voice model, and share it with the community or keep it private.",
    "train.h1": "Train a voice",
    "train.sub":
      "A few minutes of clean singing or speaking in, a voice you can use in your covers out.",
    "train.voicesTrained": "{count} voices trained",

    "train.faq.title": "Common questions",
    "train.faq.dataset.q": "What is a dataset?",
    "train.faq.dataset.a1": "A set of audio files containing vocals.",
    "train.faq.dataset.a2":
      "At least {min} minutes of singing or speaking, preferably 10+.",
    "train.faq.dataset.a3": "MP3, WAV or M4A format.",
    "train.faq.private.q": "Will my custom voice be private?",
    "train.faq.private.a":
      "Yes! Only you will be able to access, see and use your custom voice model if you choose to keep it private!",
    "train.faq.time.q": "How long will my voice take to train?",
    "train.faq.time.a":
      "It depends on how much audio you add: usually {short} for 10 minutes of audio, {long} for an hour of it. You can close the page; we'll email you when it's ready.",

    // How long training takes (lib/train/eta minutesLabel, same rounding).
    "train.eta.minutes": "about {m} minutes",
    "train.eta.hour": "about {h} hour",
    "train.eta.hours": "about {h} hours",
    "train.eta.hourMinutes": "about {h} hour {m} minutes",
    "train.eta.hoursMinutes": "about {h} hours {m} minutes",
    "train.minutes": "{n} minutes",

    // Shared by both steps' summary cards
    "train.samples": "Samples",
    "train.filesOne": "1 file · {time}",
    "train.filesMany": "{n} files · {time}",
    "train.passesLeft": "Training passes left",
    "train.unlimited": "Unlimited",
    "train.nameIt": "Name it",
    "train.creatorShort": "Training comes with Creator.",

    // Step 1: upload or record
    "train.upload.invalidType": "Invalid file type.",
    "train.upload.skippedOne":
      "Skipped {names}: already added. The same audio twice doesn't help your voice, so it only counts once.",
    "train.upload.skippedMany":
      "Skipped {names}: already added. The same audio twice doesn't help your voice, so it only counts once.",
    "train.upload.nFiles": "{n} files",
    "train.upload.someInvalid": "One or more files are invalid.",
    "train.upload.failed": "Upload failed",
    "train.upload.needsPlan": "Needs a plan to upload",
    "train.upload.tooManyLabel": "Too many uploads right now, try again soon",
    "train.upload.tooManyToast":
      "Too many uploads right now. Try again in a few minutes.",
    "train.upload.analysing":
      "Still finding the silence in your files. One moment.",
    "train.upload.blocked":
      "Some files are blocked. Please remove them to continue.",
    "train.upload.errored":
      "Some files have errors. Please remove them to continue.",
    "train.upload.processing":
      "Some files are still processing. Please wait for them to finish.",
    "train.upload.tooShort":
      "You need {min} of singing or speaking, without the silence. You have {have}.",
    "train.upload.tooLong":
      "Up to {max} of audio. You have {have}: remove a file or cut some.",
    "train.upload.title": "Add your voice",
    "train.upload.ofMin": "of {min}",
    "train.upload.overLimit": "{over} over the {n} minute limit",
    "train.upload.ready": "Ready ✓",
    "train.upload.addMore": "Add {left} more",
    "train.upload.short":
      "Voices usually sound better with {n}+ minutes. You can train now, or add {left} more if you have it.",
    "train.upload.uploadFiles": "Upload files",
    "train.upload.record": "Record",
    "train.upload.addIt": "Add it",
    "train.upload.recordHint": "Sing or talk. Record as many as you like.",
    "train.upload.blockedFrom": "Blocked - content from {label}",
    "train.upload.gettingReady": "Getting it ready...",
    "train.upload.ofVoice": "{time} of voice",
    "train.upload.removed": "{time} removed",
    "train.upload.uploading": "uploading...",
    "train.upload.done": "Done",
    "train.upload.trim": "Trim",
    "train.upload.retry": "Retry",
    "train.upload.remove": "Remove {name}",
    "train.upload.silenceNote":
      "Silence is removed for you. Purple is what your voice learns from.",
    "train.upload.tip1.title": "Just one voice",
    "train.upload.tip1.sub": "No backing singers or crowd",
    "train.upload.tip2.title": "Quiet room",
    "train.upload.tip2.sub": "No echo, no background noise",
    "train.upload.tip3.title": "More is better",
    "train.upload.tip3.sub": "Different notes and words",
    "train.upload.nextHint": "Next: a name, a picture, and who can use it.",
    "train.upload.cardTitle": "Your samples",
    "train.upload.footnote":
      "At least {min} of singing or speaking ({rec}+ sounds better), up to {max}. Silence and repeated files don't count; silence is cut for you.",
    "train.upload.footnoteNoRec":
      "At least {min} of singing or speaking, up to {max}. Silence and repeated files don't count; silence is cut for you.",
    "train.upload.creatorNote":
      "Training comes with Creator. Upload your vocals first and decide at the end; you'll see the price before you pay.",
    "train.upload.continue": "Continue",

    // A sample's play button and the trim editor
    "train.sample.play": "Play {label}",
    "train.sample.pause": "Pause {label}",
    "train.clip.keptOf": "kept of {total}",
    "train.clip.silenceCut": "{time} silence cut",
    "train.clip.cutByYou": "{time} cut by you",
    "train.clip.cut": "cut",
    "train.clip.play": "Play",
    "train.clip.stop": "Stop",
    "train.clip.keepIt": "Keep it",
    "train.clip.cutIt": "Cut it",
    "train.clip.cancel": "Cancel",
    "train.clip.hint":
      "Drag across the part you don't want, then cut it. Tap to listen.",
    "train.clip.undo": "Undo my cuts",

    // Step 2: name, picture, settings, start
    "train.info.samplesAdded": "Samples added",
    "train.info.edit": "Edit",
    "train.info.namePlaceholder": "Voice name, e.g. My voice",
    "train.info.descPlaceholder":
      "Description - let the community know more about how the voice was trained...",
    "train.info.whoCanUse": "Who can use it",
    "train.info.private": "Private, only me",
    "train.info.public": "Public in the library",
    "train.info.cleanUp": "Clean up the samples",
    "train.info.removeInstrumentals": "Remove instrumentals",
    "train.info.removeInstrumentalsSub":
      "Cleans the input data for any non-vocals in the dataset.",
    "train.info.removeReverb": "Remove reverb",
    "train.info.removeReverbSub":
      "Cleans reverb from any audio in the dataset.",
    "train.info.cardTitle": "Ready to train",
    "train.info.usuallyReadyIn": "Usually ready in",
    "train.info.upTo2h": "Up to 2 hours",
    "train.info.closePage":
      "Training runs on our side, so you can close the page.",
    "train.info.starting": "Starting...",
    "train.info.start": "Start training",
    "train.info.orGetPass": "Or get a training pass",
    "train.info.getMorePasses": "Get more training passes",
    "train.info.usesOnePass": "Uses 1 training pass",
    "train.info.started": "Training Started",
    "train.info.onCreator": "You're on Creator. Starting your training...",
    "train.info.legal":
      "By proceeding, you are giving Jammable the right to use the content you upload and CONFIRM THAT THIS CONTENT IS YOURS AND DOES NOT CONTAIN THIRD PARTY COPYRIGHT MATERIAL.",
    "train.info.agreeTerms": "Please agree to our Terms of Use!",
    "train.info.chooseName": "Please choose a voice name!",
    "train.info.uploadImage": "Please upload an image!",
    "train.info.invalidName": "Invalid model name. No special characters",
    "train.info.signIn": "User must be signed in",
    "train.info.errorStarting": "Error starting training",
    "train.info.errorSignedUrl": "Error getting signed url",

    // Demo under step 1 (components/CustomModelDemo)
    "train.demo.title": "Hear a trained voice",
    "train.demo.by": "By Jammable Power User",
    "train.demo.data.label": "Training data",
    "train.demo.data.note": "A few minutes of a voice",
    "train.demo.input.label": "Input song",
    "train.demo.input.note": "The song it will sing",
    "train.demo.output.label": "Final output",
    "train.demo.output.note": "Sung in the trained voice",
    "train.demo.play": "Play {label}",
    "train.demo.pause": "Pause {label}",

    // Failed voice card (RetryTraining). The refuse.* lines mirror the
    // messages in lib/train/retry.ts, which the API sends in English.
    "train.retry.trainAgain": "Train again",
    "train.retry.tryAgain": "Try again",
    "train.retry.orNewSamples": "Or train it with new samples",
    "train.retry.retrying": "Training again. We'll email you when it's ready.",
    "train.retry.couldntStart": "Couldn't start training. Try again.",
    "train.retry.confirmUnlimited":
      "We'll train it again on the same samples. Included in your plan.",
    "train.retry.confirmOutCreator":
      "Trying again uses a training pass, and you're out. Training comes with Creator.",
    "train.retry.confirmOut":
      "Trying again uses a training pass, and you're out.",
    "train.retry.confirmPass":
      "We'll train it again on the same samples. Uses 1 training pass (you have {passes}); if it fails again, you get it back.",
    "train.retry.cancel": "Cancel",
    "train.retry.seeCreator": "See Creator",
    "train.retry.getPass": "Get a training pass",
    "train.retry.orGetPass": "Or get a training pass",
    "train.retry.starting": "Starting...",
    "train.retry.usePass": "Use a pass and try again",
    "train.retry.refuse.cleanVocals":
      "Trying again would fail the same way. Train it again with clean vocals: no music, no long silences.",
    "train.retry.refuse.formats":
      "Trying again would fail the same way. Train it again with MP3, WAV or M4A files of clear singing or speech.",
    "train.retry.refuse.newSamples":
      "Trying again would fail the same way. Train it again with new samples.",
    "train.retry.refuse.saveAs":
      "Trying again would fail the same way. Save it as MP3 or WAV and train it again.",
    "train.retry.refuse.filesMissing": "Train it again with new samples.",
    "train.retry.refuse.retryCap":
      "This voice has been tried {n} times. Train it again, ideally with different samples.",

    // TrainFinalStage (not currently rendered)
    "train.final.confirm": "Confirm",
    "train.final.back": "Back",
    "train.final.next": "Next",
  },
  es: {
    "train.seo.title": "Clonar voz con IA: entrena tu propia voz | Jammable",
    "train.seo.description":
      "Clona tu voz con IA en Jammable: sube unos minutos de audio limpio, entrena un modelo de voz y úsalo en tus covers. Compártelo con la comunidad o mantenlo privado.",
    "train.h1": "Entrena una voz",
    "train.sub":
      "Unos minutos de voz cantada o hablada, limpia, y obtienes una voz para usar en tus covers.",
    "train.voicesTrained": "{count} voces entrenadas",

    "train.faq.title": "Preguntas frecuentes",
    "train.faq.dataset.q": "¿Qué es un dataset?",
    "train.faq.dataset.a1": "Un conjunto de archivos de audio con voz.",
    "train.faq.dataset.a2":
      "Al menos {min} minutos de voz cantada o hablada, mejor si son 10 o más.",
    "train.faq.dataset.a3": "En formato MP3, WAV o M4A.",
    "train.faq.private.q": "¿Mi voz personalizada será privada?",
    "train.faq.private.a":
      "¡Sí! Si eliges mantenerla privada, solo tú podrás ver y usar tu modelo de voz.",
    "train.faq.time.q": "¿Cuánto tarda en entrenarse mi voz?",
    "train.faq.time.a":
      "Depende de cuánto audio agregues: normalmente {short} para 10 minutos de audio y {long} para una hora. Puedes cerrar la página; te enviaremos un correo cuando esté lista.",

    "train.eta.minutes": "alrededor de {m} minutos",
    "train.eta.hour": "alrededor de {h} hora",
    "train.eta.hours": "alrededor de {h} horas",
    "train.eta.hourMinutes": "alrededor de {h} hora y {m} minutos",
    "train.eta.hoursMinutes": "alrededor de {h} horas y {m} minutos",
    "train.minutes": "{n} minutos",

    "train.samples": "Muestras",
    "train.filesOne": "1 archivo · {time}",
    "train.filesMany": "{n} archivos · {time}",
    "train.passesLeft": "Pases de entrenamiento restantes",
    "train.unlimited": "Ilimitados",
    "train.nameIt": "Ponle nombre",
    "train.creatorShort": "Entrenar viene con Creator.",

    "train.upload.invalidType": "Tipo de archivo no válido.",
    "train.upload.skippedOne":
      "Omitimos {names}: ya estaba agregado. El mismo audio dos veces no mejora tu voz, así que solo cuenta una vez.",
    "train.upload.skippedMany":
      "Omitimos {names}: ya estaban agregados. El mismo audio dos veces no mejora tu voz, así que solo cuenta una vez.",
    "train.upload.nFiles": "{n} archivos",
    "train.upload.someInvalid": "Uno o más archivos no son válidos.",
    "train.upload.failed": "No se pudo subir",
    "train.upload.needsPlan": "Necesitas un plan para subirlo",
    "train.upload.tooManyLabel":
      "Demasiadas subidas por ahora, vuelve a intentarlo pronto",
    "train.upload.tooManyToast":
      "Demasiadas subidas por ahora. Vuelve a intentarlo en unos minutos.",
    "train.upload.analysing":
      "Todavía estamos detectando los silencios de tus archivos. Un momento.",
    "train.upload.blocked":
      "Algunos archivos están bloqueados. Quítalos para continuar.",
    "train.upload.errored":
      "Algunos archivos tienen errores. Quítalos para continuar.",
    "train.upload.processing":
      "Algunos archivos todavía se están procesando. Espera a que terminen.",
    "train.upload.tooShort":
      "Necesitas {min} de voz cantada o hablada, sin contar los silencios. Tienes {have}.",
    "train.upload.tooLong":
      "Máximo {max} de audio. Tienes {have}: quita un archivo o recorta una parte.",
    "train.upload.title": "Agrega tu voz",
    "train.upload.ofMin": "de {min}",
    "train.upload.overLimit": "{over} por encima del límite de {n} minutos",
    "train.upload.ready": "Listo ✓",
    "train.upload.addMore": "Agrega {left} más",
    "train.upload.short":
      "Las voces suelen sonar mejor con {n} minutos o más. Puedes entrenar ahora, o agregar {left} más si tienes.",
    "train.upload.uploadFiles": "Subir archivos",
    "train.upload.record": "Grabar",
    "train.upload.addIt": "Agregar",
    "train.upload.recordHint":
      "Canta o habla. Graba todas las tomas que quieras.",
    "train.upload.blockedFrom": "Bloqueado: contenido de {label}",
    "train.upload.gettingReady": "Preparándolo...",
    "train.upload.ofVoice": "{time} de voz",
    "train.upload.removed": "{time} recortados",
    "train.upload.uploading": "subiendo...",
    "train.upload.done": "Listo",
    "train.upload.trim": "Recortar",
    "train.upload.retry": "Reintentar",
    "train.upload.remove": "Quitar {name}",
    "train.upload.silenceNote":
      "Quitamos los silencios por ti. Lo morado es lo que aprende tu voz.",
    "train.upload.tip1.title": "Una sola voz",
    "train.upload.tip1.sub": "Sin coros ni público",
    "train.upload.tip2.title": "Lugar silencioso",
    "train.upload.tip2.sub": "Sin eco ni ruido de fondo",
    "train.upload.tip3.title": "Más es mejor",
    "train.upload.tip3.sub": "Distintas notas y palabras",
    "train.upload.nextHint":
      "Después: un nombre, una imagen y quién puede usarla.",
    "train.upload.cardTitle": "Tus muestras",
    "train.upload.footnote":
      "Al menos {min} de voz cantada o hablada ({rec} o más suena mejor), hasta {max}. Los silencios y los archivos repetidos no cuentan; los silencios se quitan solos.",
    "train.upload.footnoteNoRec":
      "Al menos {min} de voz cantada o hablada, hasta {max}. Los silencios y los archivos repetidos no cuentan; los silencios se quitan solos.",
    "train.upload.creatorNote":
      "Entrenar viene con Creator. Sube tu voz primero y decide al final; verás el precio antes de pagar.",
    "train.upload.continue": "Continuar",

    "train.sample.play": "Reproducir {label}",
    "train.sample.pause": "Pausar {label}",
    "train.clip.keptOf": "conservados de {total}",
    "train.clip.silenceCut": "{time} de silencio quitado",
    "train.clip.cutByYou": "{time} recortado por ti",
    "train.clip.cut": "recortado",
    "train.clip.play": "Reproducir",
    "train.clip.stop": "Detener",
    "train.clip.keepIt": "Conservarlo",
    "train.clip.cutIt": "Recortarlo",
    "train.clip.cancel": "Cancelar",
    "train.clip.hint":
      "Arrastra sobre la parte que no quieres y recórtala. Toca para escuchar.",
    "train.clip.undo": "Deshacer mis recortes",

    "train.info.samplesAdded": "Muestras agregadas",
    "train.info.edit": "Editar",
    "train.info.namePlaceholder": "Nombre de la voz, p. ej. Mi voz",
    "train.info.descPlaceholder":
      "Descripción: cuéntale a la comunidad cómo se entrenó la voz...",
    "train.info.whoCanUse": "Quién puede usarla",
    "train.info.private": "Privada, solo yo",
    "train.info.public": "Pública en la biblioteca",
    "train.info.cleanUp": "Limpia las muestras",
    "train.info.removeInstrumentals": "Quitar instrumentales",
    "train.info.removeInstrumentalsSub":
      "Quita del dataset todo lo que no sea voz.",
    "train.info.removeReverb": "Quitar reverberación",
    "train.info.removeReverbSub":
      "Quita la reverberación de los audios del dataset.",
    "train.info.cardTitle": "Lista para entrenar",
    "train.info.usuallyReadyIn": "Suele estar lista en",
    "train.info.upTo2h": "Hasta 2 horas",
    "train.info.closePage":
      "El entrenamiento se hace en nuestros servidores, así que puedes cerrar la página.",
    "train.info.starting": "Iniciando...",
    "train.info.start": "Empezar a entrenar",
    "train.info.orGetPass": "O compra un pase de entrenamiento",
    "train.info.getMorePasses": "Consigue más pases de entrenamiento",
    "train.info.usesOnePass": "Usa 1 pase de entrenamiento",
    "train.info.started": "Entrenamiento iniciado",
    "train.info.onCreator": "Ya tienes Creator. Iniciando tu entrenamiento...",
    "train.info.legal":
      "Al continuar, le das a Jammable el derecho de usar el contenido que subes y CONFIRMAS QUE ESTE CONTENIDO ES TUYO Y NO CONTIENE MATERIAL CON DERECHOS DE AUTOR DE TERCEROS.",
    "train.info.agreeTerms": "¡Acepta nuestros Términos de uso!",
    "train.info.chooseName": "¡Elige un nombre para la voz!",
    "train.info.uploadImage": "¡Sube una imagen!",
    "train.info.invalidName":
      "Nombre de modelo no válido. Sin caracteres especiales",
    "train.info.signIn": "Tienes que iniciar sesión",
    "train.info.errorStarting": "Error al iniciar el entrenamiento",
    "train.info.errorSignedUrl": "Error al preparar la subida",

    "train.demo.title": "Escucha una voz entrenada",
    "train.demo.by": "Por el usuario Power de Jammable",
    "train.demo.data.label": "Datos de entrenamiento",
    "train.demo.data.note": "Unos minutos de una voz",
    "train.demo.input.label": "Canción de entrada",
    "train.demo.input.note": "La canción que va a cantar",
    "train.demo.output.label": "Resultado final",
    "train.demo.output.note": "Cantada con la voz entrenada",
    "train.demo.play": "Reproducir {label}",
    "train.demo.pause": "Pausar {label}",

    "train.retry.trainAgain": "Entrenar de nuevo",
    "train.retry.tryAgain": "Intentar de nuevo",
    "train.retry.orNewSamples": "O entrénala con muestras nuevas",
    "train.retry.retrying":
      "Entrenando de nuevo. Te enviaremos un correo cuando esté lista.",
    "train.retry.couldntStart":
      "No se pudo iniciar el entrenamiento. Inténtalo de nuevo.",
    "train.retry.confirmUnlimited":
      "La entrenaremos de nuevo con las mismas muestras. Incluido en tu plan.",
    "train.retry.confirmOutCreator":
      "Intentar de nuevo usa un pase de entrenamiento y ya no te quedan. Entrenar viene con Creator.",
    "train.retry.confirmOut":
      "Intentar de nuevo usa un pase de entrenamiento y ya no te quedan.",
    "train.retry.confirmPass":
      "La entrenaremos de nuevo con las mismas muestras. Usa 1 pase de entrenamiento (tienes {passes}); si vuelve a fallar, te lo devolvemos.",
    "train.retry.cancel": "Cancelar",
    "train.retry.seeCreator": "Ver Creator",
    "train.retry.getPass": "Comprar un pase de entrenamiento",
    "train.retry.orGetPass": "O compra un pase de entrenamiento",
    "train.retry.starting": "Iniciando...",
    "train.retry.usePass": "Usar un pase e intentar de nuevo",
    "train.retry.refuse.cleanVocals":
      "Intentarlo de nuevo fallaría igual. Entrénala otra vez con voz limpia: sin música ni silencios largos.",
    "train.retry.refuse.formats":
      "Intentarlo de nuevo fallaría igual. Entrénala otra vez con archivos MP3, WAV o M4A de voz cantada o hablada clara.",
    "train.retry.refuse.newSamples":
      "Intentarlo de nuevo fallaría igual. Entrénala otra vez con muestras nuevas.",
    "train.retry.refuse.saveAs":
      "Intentarlo de nuevo fallaría igual. Guarda el archivo como MP3 o WAV y entrénala otra vez.",
    "train.retry.refuse.filesMissing":
      "Entrénala otra vez con muestras nuevas.",
    "train.retry.refuse.retryCap":
      "Esta voz ya se intentó {n} veces. Entrénala otra vez, mejor con muestras distintas.",

    "train.final.confirm": "Confirmar",
    "train.final.back": "Atrás",
    "train.final.next": "Siguiente",
  },
  pt: {
    "train.seo.title": "Clonar voz com IA: treine sua própria voz | Jammable",
    "train.seo.description":
      "Clone sua voz com IA no Jammable: envie alguns minutos de áudio limpo, treine um modelo de voz e use nos seus covers. Compartilhe com a comunidade ou deixe privado.",
    "train.h1": "Treine uma voz",
    "train.sub":
      "Alguns minutos de voz cantada ou falada, limpa, e você ganha uma voz para usar nos seus covers.",
    "train.voicesTrained": "{count} vozes treinadas",

    "train.faq.title": "Perguntas frequentes",
    "train.faq.dataset.q": "O que é um dataset?",
    "train.faq.dataset.a1": "Um conjunto de arquivos de áudio com voz.",
    "train.faq.dataset.a2":
      "Pelo menos {min} minutos de voz cantada ou falada, de preferência 10 ou mais.",
    "train.faq.dataset.a3": "No formato MP3, WAV ou M4A.",
    "train.faq.private.q": "Minha voz personalizada vai ser privada?",
    "train.faq.private.a":
      "Sim! Se você escolher deixar privada, só você poderá ver e usar seu modelo de voz.",
    "train.faq.time.q": "Quanto tempo leva para treinar minha voz?",
    "train.faq.time.a":
      "Depende de quanto áudio você adicionar: normalmente {short} para 10 minutos de áudio e {long} para uma hora. Você pode fechar a página; avisamos por e-mail quando estiver pronta.",

    "train.eta.minutes": "cerca de {m} minutos",
    "train.eta.hour": "cerca de {h} hora",
    "train.eta.hours": "cerca de {h} horas",
    "train.eta.hourMinutes": "cerca de {h} hora e {m} minutos",
    "train.eta.hoursMinutes": "cerca de {h} horas e {m} minutos",
    "train.minutes": "{n} minutos",

    "train.samples": "Amostras",
    "train.filesOne": "1 arquivo · {time}",
    "train.filesMany": "{n} arquivos · {time}",
    "train.passesLeft": "Passes de treinamento restantes",
    "train.unlimited": "Ilimitados",
    "train.nameIt": "Dê um nome",
    "train.creatorShort": "Treinar vem com o Creator.",

    "train.upload.invalidType": "Tipo de arquivo inválido.",
    "train.upload.skippedOne":
      "Ignoramos {names}: já tinha sido adicionado. O mesmo áudio duas vezes não melhora sua voz, então só conta uma vez.",
    "train.upload.skippedMany":
      "Ignoramos {names}: já tinham sido adicionados. O mesmo áudio duas vezes não melhora sua voz, então só conta uma vez.",
    "train.upload.nFiles": "{n} arquivos",
    "train.upload.someInvalid": "Um ou mais arquivos são inválidos.",
    "train.upload.failed": "Falha no envio",
    "train.upload.needsPlan": "Precisa de um plano para enviar",
    "train.upload.tooManyLabel":
      "Muitos envios agora, tente de novo daqui a pouco",
    "train.upload.tooManyToast":
      "Muitos envios agora. Tente de novo em alguns minutos.",
    "train.upload.analysing":
      "Ainda estamos encontrando os silêncios nos seus arquivos. Um momento.",
    "train.upload.blocked":
      "Alguns arquivos estão bloqueados. Remova-os para continuar.",
    "train.upload.errored":
      "Alguns arquivos têm erros. Remova-os para continuar.",
    "train.upload.processing":
      "Alguns arquivos ainda estão sendo processados. Aguarde até terminarem.",
    "train.upload.tooShort":
      "Você precisa de {min} de voz cantada ou falada, sem contar os silêncios. Você tem {have}.",
    "train.upload.tooLong":
      "No máximo {max} de áudio. Você tem {have}: remova um arquivo ou corte uma parte.",
    "train.upload.title": "Adicione sua voz",
    "train.upload.ofMin": "de {min}",
    "train.upload.overLimit": "{over} acima do limite de {n} minutos",
    "train.upload.ready": "Pronto ✓",
    "train.upload.addMore": "Adicione mais {left}",
    "train.upload.short":
      "As vozes costumam soar melhor com {n} minutos ou mais. Você pode treinar agora ou adicionar mais {left}, se tiver.",
    "train.upload.uploadFiles": "Enviar arquivos",
    "train.upload.record": "Gravar",
    "train.upload.addIt": "Adicionar",
    "train.upload.recordHint": "Cante ou fale. Grave quantas vezes quiser.",
    "train.upload.blockedFrom": "Bloqueado: conteúdo de {label}",
    "train.upload.gettingReady": "Preparando...",
    "train.upload.ofVoice": "{time} de voz",
    "train.upload.removed": "{time} removidos",
    "train.upload.uploading": "enviando...",
    "train.upload.done": "Pronto",
    "train.upload.trim": "Cortar",
    "train.upload.retry": "Tentar de novo",
    "train.upload.remove": "Remover {name}",
    "train.upload.silenceNote":
      "Os silêncios são removidos para você. O roxo é o que sua voz aprende.",
    "train.upload.tip1.title": "Uma voz só",
    "train.upload.tip1.sub": "Sem backing vocals nem plateia",
    "train.upload.tip2.title": "Ambiente silencioso",
    "train.upload.tip2.sub": "Sem eco nem barulho de fundo",
    "train.upload.tip3.title": "Quanto mais, melhor",
    "train.upload.tip3.sub": "Notas e palavras variadas",
    "train.upload.nextHint": "Depois: um nome, uma imagem e quem pode usar.",
    "train.upload.cardTitle": "Suas amostras",
    "train.upload.footnote":
      "Pelo menos {min} de voz cantada ou falada ({rec} ou mais soa melhor), até {max}. Silêncios e arquivos repetidos não contam; os silêncios são cortados automaticamente.",
    "train.upload.footnoteNoRec":
      "Pelo menos {min} de voz cantada ou falada, até {max}. Silêncios e arquivos repetidos não contam; os silêncios são cortados automaticamente.",
    "train.upload.creatorNote":
      "Treinar vem com o Creator. Envie sua voz primeiro e decida no final; você vê o preço antes de pagar.",
    "train.upload.continue": "Continuar",

    "train.sample.play": "Tocar {label}",
    "train.sample.pause": "Pausar {label}",
    "train.clip.keptOf": "mantidos de {total}",
    "train.clip.silenceCut": "{time} de silêncio cortado",
    "train.clip.cutByYou": "{time} cortado por você",
    "train.clip.cut": "cortado",
    "train.clip.play": "Tocar",
    "train.clip.stop": "Parar",
    "train.clip.keepIt": "Manter",
    "train.clip.cutIt": "Cortar",
    "train.clip.cancel": "Cancelar",
    "train.clip.hint":
      "Arraste sobre a parte que você não quer e corte. Toque para ouvir.",
    "train.clip.undo": "Desfazer meus cortes",

    "train.info.samplesAdded": "Amostras adicionadas",
    "train.info.edit": "Editar",
    "train.info.namePlaceholder": "Nome da voz, ex.: Minha voz",
    "train.info.descPlaceholder":
      "Descrição: conte para a comunidade como a voz foi treinada...",
    "train.info.whoCanUse": "Quem pode usar",
    "train.info.private": "Privada, só eu",
    "train.info.public": "Pública na biblioteca",
    "train.info.cleanUp": "Limpe as amostras",
    "train.info.removeInstrumentals": "Remover instrumentais",
    "train.info.removeInstrumentalsSub":
      "Remove do dataset tudo o que não for voz.",
    "train.info.removeReverb": "Remover reverb",
    "train.info.removeReverbSub": "Remove o reverb dos áudios do dataset.",
    "train.info.cardTitle": "Pronta para treinar",
    "train.info.usuallyReadyIn": "Normalmente fica pronta em",
    "train.info.upTo2h": "Até 2 horas",
    "train.info.closePage":
      "O treinamento roda nos nossos servidores, então você pode fechar a página.",
    "train.info.starting": "Iniciando...",
    "train.info.start": "Começar o treinamento",
    "train.info.orGetPass": "Ou compre um passe de treinamento",
    "train.info.getMorePasses": "Compre mais passes de treinamento",
    "train.info.usesOnePass": "Usa 1 passe de treinamento",
    "train.info.started": "Treinamento iniciado",
    "train.info.onCreator":
      "Você está no Creator. Iniciando seu treinamento...",
    "train.info.legal":
      "Ao continuar, você dá ao Jammable o direito de usar o conteúdo que envia e CONFIRMA QUE ESTE CONTEÚDO É SEU E NÃO CONTÉM MATERIAL PROTEGIDO POR DIREITOS AUTORAIS DE TERCEIROS.",
    "train.info.agreeTerms": "Aceite nossos Termos de Uso!",
    "train.info.chooseName": "Escolha um nome para a voz!",
    "train.info.uploadImage": "Envie uma imagem!",
    "train.info.invalidName":
      "Nome de modelo inválido. Sem caracteres especiais",
    "train.info.signIn": "Você precisa entrar na sua conta",
    "train.info.errorStarting": "Erro ao iniciar o treinamento",
    "train.info.errorSignedUrl": "Erro ao preparar o envio",

    "train.demo.title": "Ouça uma voz treinada",
    "train.demo.by": "Por um usuário Power do Jammable",
    "train.demo.data.label": "Dados de treinamento",
    "train.demo.data.note": "Alguns minutos de uma voz",
    "train.demo.input.label": "Música de entrada",
    "train.demo.input.note": "A música que ela vai cantar",
    "train.demo.output.label": "Resultado final",
    "train.demo.output.note": "Cantada com a voz treinada",
    "train.demo.play": "Tocar {label}",
    "train.demo.pause": "Pausar {label}",

    "train.retry.trainAgain": "Treinar de novo",
    "train.retry.tryAgain": "Tentar de novo",
    "train.retry.orNewSamples": "Ou treine com novas amostras",
    "train.retry.retrying":
      "Treinando de novo. Avisamos por e-mail quando estiver pronta.",
    "train.retry.couldntStart":
      "Não foi possível iniciar o treinamento. Tente de novo.",
    "train.retry.confirmUnlimited":
      "Vamos treinar de novo com as mesmas amostras. Incluído no seu plano.",
    "train.retry.confirmOutCreator":
      "Tentar de novo usa um passe de treinamento, e você não tem mais. Treinar vem com o Creator.",
    "train.retry.confirmOut":
      "Tentar de novo usa um passe de treinamento, e você não tem mais.",
    "train.retry.confirmPass":
      "Vamos treinar de novo com as mesmas amostras. Usa 1 passe de treinamento (você tem {passes}); se falhar de novo, devolvemos o passe.",
    "train.retry.cancel": "Cancelar",
    "train.retry.seeCreator": "Ver o Creator",
    "train.retry.getPass": "Comprar um passe de treinamento",
    "train.retry.orGetPass": "Ou compre um passe de treinamento",
    "train.retry.starting": "Iniciando...",
    "train.retry.usePass": "Usar um passe e tentar de novo",
    "train.retry.refuse.cleanVocals":
      "Tentar de novo falharia do mesmo jeito. Treine de novo com voz limpa: sem música nem silêncios longos.",
    "train.retry.refuse.formats":
      "Tentar de novo falharia do mesmo jeito. Treine de novo com arquivos MP3, WAV ou M4A de voz cantada ou falada nítida.",
    "train.retry.refuse.newSamples":
      "Tentar de novo falharia do mesmo jeito. Treine de novo com novas amostras.",
    "train.retry.refuse.saveAs":
      "Tentar de novo falharia do mesmo jeito. Salve o arquivo como MP3 ou WAV e treine de novo.",
    "train.retry.refuse.filesMissing": "Treine de novo com novas amostras.",
    "train.retry.refuse.retryCap":
      "Esta voz já foi tentada {n} vezes. Treine de novo, de preferência com amostras diferentes.",

    "train.final.confirm": "Confirmar",
    "train.final.back": "Voltar",
    "train.final.next": "Próximo",
  },
};
